import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { useTranslate } from "src/locales/use-locales";
import type { CapriniRiskAssessmentData, CapriniRiskAssessmentProps } from "./models";
import { SCORE_FIELDS } from "./constants/scoreFields";
import { RISK_FACTOR_CONFIGS, CONDITIONAL_FIELDS } from "./constants/riskFactorConfig";
import { shouldShowConditionalFields } from "./constants/fieldHelpers";
import { ErrorSnackbar } from "./components/ErrorSnackbar";
import { FormHeader } from "./components/FormHeader";
import { SectionHeader } from "./components/SectionHeader";
import { RiskFactorList } from "./components/RiskFactorList";
import { ScoreDisplay } from "./components/ScoreDisplay";
import { SaveButtonWrapper } from "./components/SaveButtonWrapper";

function CapriniRiskAssessmentCreate({
  recordId,
  patientId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: CapriniRiskAssessmentProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "CAPRINI_RISK_ASSESSMENT",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof CapriniRiskAssessmentData, value as string | number | Date | null);
      });
    },
  });

  const typedFormData = formData as CapriniRiskAssessmentData;
  const previousFormDataRef = useRef<string>('');

  // Optimize formData sync - only sync when formData actually changes
  useEffect(() => {
    const currentFormDataString = JSON.stringify(formData);
    if (currentFormDataString !== previousFormDataRef.current) {
      Object.entries(formData).forEach(([key, value]) => {
        parentHandleChange(key as keyof CapriniRiskAssessmentData, value as string | number | Date | null);
      });
      previousFormDataRef.current = currentFormDataString;
    }
  }, [formData, parentHandleChange]);

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  const [score, setScore] = useState(0);

  const calculateScore = useCallback((formData: CapriniRiskAssessmentData): number => {
    return SCORE_FIELDS.reduce((sum, field) => {
      const value = formData[field as keyof CapriniRiskAssessmentData];
      if (value === null || value === undefined) {
        return sum;
      }
      if (typeof value === 'string') {
        const numValue = Number(value);
        return sum + (isNaN(numValue) ? 0 : numValue);
      }
      if (typeof value === 'number') {
      return sum + value;
      }
      // Date veya diğer tipler için 0 döndür
      return sum;
    }, 0);
  }, []);

  const handleScoreCalculate = useCallback(() => {
    if (!typedFormData) return;
    const calculatedScore = calculateScore(typedFormData);
    setScore(calculatedScore);
    handleChange("SCORE", calculatedScore);
  }, [typedFormData, calculateScore, handleChange]);

  // Optimize dependency array by using a serialized version of relevant fields
  const scoreDependency = useMemo(() => {
    return SCORE_FIELDS.map(field => typedFormData[field as keyof CapriniRiskAssessmentData]).join(',');
  }, [typedFormData]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate, scoreDependency]);

  const showConditionalFields = useMemo(
    () => shouldShowConditionalFields(patientData),
    [patientData]
  );

  return (
    <>
      <ErrorSnackbar error={error} onClose={() => setError(null)} />

      <Box sx={{ padding: "20px", width: "100%" }}>
        <SaveButtonWrapper
          onSave={() => save(typedFormData.CRA_ID)}
          isSaving={isSaving}
          extraAccess={extraAccess}
        />

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <FormHeader
              formData={typedFormData}
              onFieldChange={handleChange}
              extraAccess={extraAccess}
            />

            <SectionHeader labelKey="caprini-risk-assessment.existing-risk-factors" />

            <RiskFactorList
              configs={RISK_FACTOR_CONFIGS}
              cardData={cardData}
              selectedMCPs={selectedMCPs}
              handleMCPChange={handleMCPChange}
              getMCPParameterDetails={getMCPParameterDetails}
              mcpCacheLoaded={mcpCacheLoaded}
              extraAccess={extraAccess}
            />

            {showConditionalFields && (
              <RiskFactorList
                configs={CONDITIONAL_FIELDS}
                cardData={cardData}
                selectedMCPs={selectedMCPs}
                handleMCPChange={handleMCPChange}
                getMCPParameterDetails={getMCPParameterDetails}
                mcpCacheLoaded={mcpCacheLoaded}
                extraAccess={extraAccess}
              />
            )}

            <ScoreDisplay score={score} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default CapriniRiskAssessmentCreate;
